import { useRef, useState } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Box, Button, Rating, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { Wizard } from '@ui-framework/index';
import {
  ASSIGNED_SP_DISPATCH_METHOD,
  DISPATCH_METHODS,
} from '@constants/index';
import {
  useServiceJob,
  IconLibrary,
  useServiceJobDispatchAndRescheduleOptions,
} from '@product-framework/index';
import PresetCalendar from './views/PresetCalendar';
import OfferBased from './views/OfferBased';
import NoDispatchMethodFound from './views/NoDispatchMethodFound';
import ServiceDateForm from './components/ServiceDateForm';
import WrapWithQuestion from './views/WrapWithQuestion';

function ServiceJobDispatchWizard({ isRescheduleFlow = false }) {
  const wizardRef = useRef();
  const {
    dispatchOptions,
    DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN,
  } = useServiceJobDispatchAndRescheduleOptions();
  const { serviceJob } = useServiceJob();
  const [useCurrenSPQuestionAnswered, setUseCurrenSPQuestionAnswered] =
    useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const DISPATCH_STEPS_CONFIG = {
    // SP Re-Scheduling Job Assigned to him
    [DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN]: {
      component: (
        <ServiceDateForm
          preferredServiceProviderId={serviceJob?.serviceProviderId}
          isRescheduleFlow={isRescheduleFlow}
          rescheduleBySP
        />
      ),
      name: `${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.RESPONSIVE_INTEGRATION}`,
      wizardStepName: '',
      wizardTitle: '',
      icon: <IconLibrary.DispatchAlternateTime />,
    },
    // SA Scheduling Assigned Job
    [`${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.PRESET_CALENDAR_BASED_SCHEDULING}`]:
      {
        component: (
          <WrapWithQuestion
            useCurrenSPQuestionAnswered={useCurrenSPQuestionAnswered}
            setUseCurrenSPQuestionAnswered={setUseCurrenSPQuestionAnswered}
            wizardRef={wizardRef}>
            <PresetCalendar
              isRescheduleFlow={isRescheduleFlow}
              serviceProviderId={serviceJob?.serviceProviderId}
            />
          </WrapWithQuestion>
        ),
        name: `${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.PRESET_CALENDAR_BASED_SCHEDULING}`,
        wizardStepName: 'Assigned Service Provider',
        wizardTitle: 'Reschedule with Assigned Service Provider',
        icon: <IconLibrary.LiveCalendar />,
      },
    // SA Scheduling Assigned Job
    [`${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.RESPONSIVE_INTEGRATION}`]:
      {
        component: (
          <WrapWithQuestion
            useCurrenSPQuestionAnswered={useCurrenSPQuestionAnswered}
            setUseCurrenSPQuestionAnswered={setUseCurrenSPQuestionAnswered}
            wizardRef={wizardRef}>
            <ServiceDateForm
              preferredServiceProviderId={serviceJob?.serviceProviderId}
              isRescheduleFlow={isRescheduleFlow}
              rescheduleBySP={false}
            />
            ,
          </WrapWithQuestion>
        ),
        name: `${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.RESPONSIVE_INTEGRATION}`,
        wizardStepName: 'Assigned Service Provider',
        wizardTitle: 'Reschedule with Assigned Service Provider',
        icon: <IconLibrary.DispatchAlternateTime />,
      },
    // SA Scheduling Assigned Job
    [`${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.MANUAL}`]: {
      component: <Navigate to={`/servicejobs/map/${serviceJob.id}`} />,
      name: `${ASSIGNED_SP_DISPATCH_METHOD}_${DISPATCH_METHODS.MANUAL}`,
      wizardStepName: 'Use Map Scheduling',
      wizardTitle: 'Map Scheduling',
      icon: <IconLibrary.Map />,
    },
    // SA Schedulign New Job or UnAssigned Job
    [DISPATCH_METHODS.PRESET_CALENDAR_BASED_SCHEDULING]: {
      component: <PresetCalendar isRescheduleFlow={isRescheduleFlow} />,
      name: DISPATCH_METHODS.PRESET_CALENDAR_BASED_SCHEDULING,
      wizardStepName: 'Use Calendar Scheduling',
      wizardTitle: 'Calendar Scheduling',
      icon: <IconLibrary.LiveCalendar />,
    },
    // SA Schedulign New Job or UnAssigned Job
    [DISPATCH_METHODS.RESPONSIVE_INTEGRATION]: {
      component: <OfferBased isRescheduleFlow={isRescheduleFlow} />,
      name: DISPATCH_METHODS.RESPONSIVE_INTEGRATION,
      wizardStepName: 'Use Offer-Based Scheduling',
      wizardTitle: 'Offer-Based Scheduling',
      icon: <IconLibrary.DispatchAlternateTime />,
    },
    // SA Schedulign New Job or UnAssigned Job
    [DISPATCH_METHODS.MANUAL]: {
      component: <Navigate to={`/servicejobs/map/${serviceJob.id}`} />,
      name: DISPATCH_METHODS.RESPONSIVE_INTEGRATION,
      wizardStepName: 'Use Map Scheduling',
      wizardTitle: 'Map Scheduling',
      icon: <IconLibrary.Map />,
    },
  };

  const steps = [];

  if (Array.isArray(dispatchOptions) && dispatchOptions.length > 0) {
    for (let i = 0; i <= dispatchOptions.length - 1; i += 1) {
      if (DISPATCH_STEPS_CONFIG?.[dispatchOptions?.[i]?.dispatchMethod]) {
        steps.push(DISPATCH_STEPS_CONFIG[dispatchOptions[i].dispatchMethod]);
      }
    }
  }

  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;

  const onStepChange = step => {
    setActiveStep(step);
  };

  if (dispatchOptions?.[0]?.dispatchMethod === DISPATCH_METHODS.MANUAL) {
    return <Navigate to={`/servicejobs/map/${serviceJob.id}`} />;
  }

  return (
    <Box>
      {Array.isArray(steps) && steps.length > 1 && (
        <Box>
          <Grid2 container>
            <Grid2 xs={4}>
              {!isFirst && (
                <Button
                  variant="outlined"
                  endIcon={steps[activeStep - 1].icon}
                  onClick={() => wizardRef?.current?.previous()}>
                  {steps[activeStep - 1].wizardStepName}{' '}
                  <Rating
                    value={Number(dispatchOptions[activeStep - 1]?.score)}
                    readOnly
                    size="small"
                    sx={{
                      '& .MuiRating-iconEmpty': {
                        display: 'none',
                      },
                      '& .MuiRating-iconFilled svg': {
                        color: '#FFC107',
                        stroke: '#8c742c',
                        strokeWidth: 0.8,
                      },
                    }}
                  />
                </Button>
              )}
            </Grid2>
            <Grid2 xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {steps[activeStep].wizardTitle}
              </Typography>
            </Grid2>
            <Grid2 xs={4} sx={{ textAlign: 'right' }}>
              {!isLast && (
                <Button
                  variant="outlined"
                  onClick={() => wizardRef?.current?.next()}
                  startIcon={steps[activeStep + 1].icon}>
                  {steps[activeStep + 1].wizardStepName}
                  <Rating
                    value={Number(dispatchOptions[activeStep + 1]?.score)}
                    readOnly
                    size="small"
                    sx={{
                      '& .MuiRating-iconEmpty': {
                        display: 'none',
                      },
                      '& .MuiRating-iconFilled svg': {
                        color: '#FFC107',
                        stroke: '#8c742c',
                        strokeWidth: 0.8,
                      },
                    }}
                  />
                </Button>
              )}
            </Grid2>
          </Grid2>
        </Box>
      )}
      {Array.isArray(steps) && steps.length === 0 && <NoDispatchMethodFound />}
      <Wizard
        ref={wizardRef}
        activeStep={activeStep}
        onStepChange={onStepChange}
        steps={steps}
      />
    </Box>
  );
}

export default ServiceJobDispatchWizard;
